"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  bulkToggleInventoryWebsites,
  createInventoryWebsite,
  getInventoryWebsite,
  getInventoryWebsites,
  normalizeInventoryWebsiteStatusKeys,
  updateInventoryWebsite,
} from "../api";

export const fetchAllInventoryWebsitePages = async ({
  search = "",
  status = "all",
  statuses = [],
  pageSize = 100,
} = {}) => {
  const safePageSize = Math.min(100, Math.max(1, Number(pageSize) || 100));
  const firstPage = await getInventoryWebsites({
    page: 1,
    pageSize: safePageSize,
    search,
    status,
    statuses,
  });
  const pageCount = Math.max(
    1,
    Number(firstPage?.pagination?.pageCount) ||
      Math.ceil(Number(firstPage?.pagination?.total || 0) / safePageSize) ||
      1
  );
  const remainingPages =
    pageCount > 1
      ? await Promise.all(
          Array.from({ length: pageCount - 1 }, (_, index) =>
            getInventoryWebsites({
              page: index + 2,
              pageSize: safePageSize,
              search,
              status,
              statuses,
            })
          )
        )
      : [];
  const results = [firstPage, ...remainingPages].flatMap((result) =>
    Array.isArray(result?.results) ? result.results : []
  );

  return {
    ...firstPage,
    results,
    pagination: {
      ...(firstPage?.pagination || {}),
      page: 1,
      pageSize: safePageSize,
      pageCount,
      total: Number(firstPage?.pagination?.total ?? results.length),
    },
  };
};

export const useGetInventoryWebsites = ({
  page = 1,
  pageSize = 25,
  search = "",
  status = "all",
  statuses = [],
  enabled = true,
  fetchAll = false,
} = {}) => {
  const normalizedStatusKeys = normalizeInventoryWebsiteStatusKeys(statuses);
  const statusesKey = normalizedStatusKeys.join(",");
  return useQuery({
    queryKey: [
      "inventory-websites",
      page,
      pageSize,
      search,
      status,
      statusesKey,
      fetchAll ? "all-pages" : "single-page",
    ],
    queryFn: () =>
      fetchAll
        ? fetchAllInventoryWebsitePages({
            search,
            status,
            statuses: normalizedStatusKeys,
            pageSize,
          })
        : getInventoryWebsites({
            page,
            pageSize,
            search,
            status,
            statuses: normalizedStatusKeys,
          }),
    enabled,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });
};

export const useGetInventoryWebsite = (id) => {
  return useQuery({
    queryKey: ["inventory-website", id],
    queryFn: () => getInventoryWebsite(id),
    enabled: Boolean(id),
  });
};

export const useCreateInventoryWebsite = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => createInventoryWebsite(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["inventory-websites"] });
    },
  });
};

export const useUpdateInventoryWebsite = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data, files }) => updateInventoryWebsite(id, data, files),
    onSuccess: async (_result, variables) => {
      await queryClient.invalidateQueries({ queryKey: ["inventory-websites"] });
      if (variables?.id) {
        await queryClient.invalidateQueries({ queryKey: ["inventory-website", variables.id] });
      }
    },
  });
};

export const useBulkToggleInventoryWebsites = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids) => bulkToggleInventoryWebsites(ids),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["inventory-websites"] });
      await queryClient.invalidateQueries({ queryKey: ["inventory-website"] });
    },
  });
};
